import React, { useEffect, useState } from "react";
import { X, ScrollText } from "lucide-react";
import { api } from "../../api";

const EMPTY = {
  oggetto: "",
  qualifica: "",
  nodo: "",
  nome: "",
  email: "",
  messaggio: "",
};

/**
 * Protocollo di Contatto Ufficiale — canale istituzionale per nodi del Network 6AMICO.
 */
export default function ProtocolloContattoModal({ open, onClose, settore = "", oggettoPreset = "" }) {
  const [form, setForm] = useState(EMPTY);
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setSubmitted(false);
    setError("");
    setForm({
      ...EMPTY,
      oggetto: oggettoPreset || (settore ? `Richiesta protocollo — ${settore}` : ""),
    });
  }, [open, settore, oggettoPreset]);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.oggetto.trim() || !form.qualifica.trim() || !form.nodo.trim()) {
      setError("Compilare Oggetto, Qualifica e Nodo di riferimento — campi obbligatori del protocollo.");
      return;
    }
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      setError("Indirizzo email non valido.");
      return;
    }
    setSending(true);
    try {
      await api.post("/protocollo/contatti", {
        oggetto: form.oggetto.trim(),
        qualifica: form.qualifica.trim(),
        nodo: form.nodo.trim(),
        settore: settore || null,
        nome: form.nome.trim() || null,
        email: form.email.trim() || null,
        messaggio: form.messaggio.trim() || null,
      });
      setSubmitted(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Invio non riuscito — riprova fra qualche minuto.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/85"
      role="dialog"
      aria-modal="true"
      aria-labelledby="protocollo-contatto-title"
      data-testid="protocollo-contatto-modal"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-lg border border-amber-800/40 bg-slate-900 text-slate-300 shadow-none"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 p-1.5 rounded text-slate-400 hover:text-slate-200 transition-colors"
          aria-label="Chiudi"
          data-testid="protocollo-contatto-close"
        >
          <X size={18} aria-hidden />
        </button>

        <header className="px-6 pt-6 pb-4 border-b border-slate-800">
          <p className="protocollo-suite-kicker mb-2 flex items-center gap-2">
            <ScrollText size={12} className="text-amber-700" aria-hidden />
            Protocollo 2013
          </p>
          <h2 id="protocollo-contatto-title" className="font-serif text-2xl text-white pr-8">
            Protocollo di Contatto Ufficiale
          </h2>
          {settore && (
            <p className="text-sm text-slate-400 mt-2 font-light">
              Settore: <span className="text-slate-200">{settore}</span>
            </p>
          )}
          <p className="text-xs text-slate-500 mt-3 leading-relaxed">
            Canale riservato alle richieste istituzionali verso il Network 6AMICO. I campi contrassegnati
            definiscono l&apos;inquadramento protocollo della pratica.
          </p>
        </header>

        {submitted ? (
          <div className="px-6 py-10 text-center" data-testid="protocollo-contatto-success">
            <p className="font-serif text-xl text-white mb-3">Richiesta registrata</p>
            <p className="text-sm text-slate-400 leading-relaxed max-w-sm mx-auto">
              La pratica è stata acquisita nel registro protocollo. Un referente del nodo centrale risponderà
              nei termini previsti dalla governance territoriale.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="mt-8 px-6 py-2.5 rounded bg-amber-700 text-slate-950 hover:bg-amber-800 font-semibold text-sm transition-colors"
            >
              Chiudi
            </button>
          </div>
        ) : (
          <form className="px-6 py-5 space-y-4" onSubmit={handleSubmit} data-testid="protocollo-contatto-form">
            <fieldset className="space-y-4 border-0 p-0">
              <legend className="text-[10px] font-mono uppercase tracking-[0.2em] text-amber-700/90 mb-1">
                Campi protocollo (obbligatori)
              </legend>

              <label className="block">
                <span className="text-xs text-slate-400 uppercase tracking-wide">Oggetto della richiesta *</span>
                <input
                  type="text"
                  required
                  value={form.oggetto}
                  onChange={set("oggetto")}
                  className="mt-1.5 w-full rounded border border-slate-700 bg-slate-950 px-3 py-2.5 text-sm text-white placeholder:text-slate-600 focus:border-amber-800/60 focus:outline-none"
                  placeholder="Es. Adesione nodo, gemellaggio, documentazione"
                  data-testid="protocollo-contatto-oggetto"
                />
              </label>

              <label className="block">
                <span className="text-xs text-slate-400 uppercase tracking-wide">Qualifica *</span>
                <input
                  type="text"
                  required
                  value={form.qualifica}
                  onChange={set("qualifica")}
                  className="mt-1.5 w-full rounded border border-slate-700 bg-slate-950 px-3 py-2.5 text-sm text-white placeholder:text-slate-600 focus:border-amber-800/60 focus:outline-none"
                  placeholder="Es. Sindaco, Rettore, Presidente Pro Loco"
                  data-testid="protocollo-contatto-qualifica"
                />
              </label>

              <label className="block">
                <span className="text-xs text-slate-400 uppercase tracking-wide">Nodo (Borgo/Ente) di riferimento *</span>
                <input
                  type="text"
                  required
                  value={form.nodo}
                  onChange={set("nodo")}
                  className="mt-1.5 w-full rounded border border-slate-700 bg-slate-950 px-3 py-2.5 text-sm text-white placeholder:text-slate-600 focus:border-amber-800/60 focus:outline-none"
                  placeholder="Comune, parrocchia, ateneo o ente"
                  data-testid="protocollo-contatto-nodo"
                />
              </label>
            </fieldset>

            <fieldset className="space-y-4 border-0 p-0 pt-2 border-t border-slate-800">
              <legend className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500 mb-1 pt-3">
                Identificazione (facoltativa)
              </legend>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="block">
                  <span className="text-xs text-slate-500">Nome</span>
                  <input
                    type="text"
                    value={form.nome}
                    onChange={set("nome")}
                    className="mt-1.5 w-full rounded border border-slate-800 bg-slate-950/80 px-3 py-2 text-sm text-white focus:border-amber-800/40 focus:outline-none"
                    data-testid="protocollo-contatto-nome"
                  />
                </label>
                <label className="block">
                  <span className="text-xs text-slate-500">Email</span>
                  <input
                    type="email"
                    value={form.email}
                    onChange={set("email")}
                    className="mt-1.5 w-full rounded border border-slate-800 bg-slate-950/80 px-3 py-2 text-sm text-white focus:border-amber-800/40 focus:outline-none"
                    data-testid="protocollo-contatto-email"
                  />
                </label>
              </div>

              <label className="block">
                <span className="text-xs text-slate-500">Messaggio</span>
                <textarea
                  rows={3}
                  value={form.messaggio}
                  onChange={set("messaggio")}
                  className="mt-1.5 w-full rounded border border-slate-800 bg-slate-950/80 px-3 py-2 text-sm text-white resize-y min-h-[80px] focus:border-amber-800/40 focus:outline-none"
                  placeholder="Dettaglio della richiesta istituzionale"
                  data-testid="protocollo-contatto-messaggio"
                />
              </label>
            </fieldset>

            {error && (
              <p className="text-sm text-amber-600/90" role="alert" data-testid="protocollo-contatto-error">
                {error}
              </p>
            )}

            <div className="flex flex-wrap gap-3 pt-2 pb-4">
              <button
                type="submit"
                disabled={sending}
                className="px-5 py-2.5 rounded bg-amber-700 text-slate-950 hover:bg-amber-800 font-semibold text-sm transition-colors disabled:opacity-60"
                data-testid="protocollo-contatto-submit"
              >
                {sending ? "Invio in corso…" : "Inoltra al registro protocollo"}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="px-5 py-2.5 rounded border border-slate-700 text-slate-400 hover:text-slate-200 text-sm transition-colors"
              >
                Annulla
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
